import { useQuery } from '@tanstack/react-query';
import { accountUsageApi, type AccountUsageSummary } from '../services/api';

/**
 * Shared current-period account usage query (task 676).
 *
 * `GET /api/accounts/usage` is the account-scoped, member-reachable summary of
 * the current billing period. Since task 680 it additively carries the
 * account's `plan` slug and its `limits`, resolved server-side from the live
 * AMS plan resolver — which is what let task 676 drop the `plan` and `limits`
 * JWT claims. This module is the single definition of that query: five
 * surfaces (DashboardPage, BillingPage, SettingsBillingPage, SubscriptionPage,
 * UsageIndicator) previously each declared their own copy of it under the same
 * key, and the plan-driven UI has now joined them.
 *
 * IMPORTANT: this file must not import AuthContext. AuthProvider consumes the
 * query for the analytics `plan` property, so an import back the other way
 * would be a cycle. Callers that need to hold the request until a token exists
 * pass `enabled` instead (AuthProvider does exactly that).
 */

/** The query key every account-usage reader shares. */
export const ACCOUNT_USAGE_QUERY_KEY = ['accountUsage', 'current'] as const;

interface UseAccountUsageOptions {
  /**
   * Gate the request. Defaults to true. AuthProvider passes its
   * `isAuthenticated` so the call never fires before a bearer token exists —
   * an unauthenticated 401 would otherwise poison the cache with a null for a
   * whole refetch interval, hiding the plan badge for two minutes after login.
   */
  enabled?: boolean;
}

/**
 * The current period's usage summary, or `null` when the endpoint is
 * unavailable (not deployed, not permitted, network error). Returning null
 * rather than throwing preserves the graceful degradation every existing
 * consumer already relies on: they render their usage sections only when the
 * summary is present.
 */
export const useAccountUsage = ({ enabled = true }: UseAccountUsageOptions = {}) =>
  useQuery({
    queryKey: ACCOUNT_USAGE_QUERY_KEY,
    queryFn: async (): Promise<AccountUsageSummary | null> => {
      try {
        const res = await accountUsageApi.getCurrentPeriod();
        return res.data.data;
      } catch {
        // Usage endpoint unavailable — degrade gracefully rather than surface
        // an error state. `null` is the "could not resolve" signal below.
        return null;
      }
    },
    enabled,
    refetchInterval: 120_000,
    staleTime: 60_000,
  });

/** The account's plan as the UI needs it: a value plus whether it is known yet. */
export interface AccountPlanState {
  /**
   * The plan slug, or `null` when the account verifiably has no plan. Only
   * meaningful once `isResolved` is true — it is also `null` while loading.
   */
  plan: string | null;
  /** The plan's limits; empty until resolved. -1 means unlimited. */
  limits: Record<string, number>;
  /**
   * True once the server has actually answered. The distinction matters: a
   * plan-less account and an unanswered query both present as `plan === null`,
   * and treating the second as the first would flash "No plan" on every load
   * and momentarily disable the AI entry points. Every *ambient* no-plan
   * affordance (PlanBadge, NoPlanBanner, useRequiresPlan, the onboarding
   * checklist) must therefore require `isResolved` before it renders.
   */
  isResolved: boolean;
  /**
   * True while the request is still in flight — i.e. not yet answered AND not
   * failed. The billing pages, whose whole subject is the plan, hold their
   * render on this and then treat "no plan slug" as the empty state whether
   * that came from a genuine plan-less account or a failed lookup. Ambient
   * surfaces use `isResolved` instead, because for them a wrong "No plan" is
   * worse than showing nothing.
   */
  isPending: boolean;
}

/**
 * Plan + limits for the signed-in account, read from the shared usage query.
 *
 * Replaces the `plan` and `limits` JWT claims (task 676). A failed request
 * resolves to `null` from `useAccountUsage`, which reads here as "not
 * resolved" — so every consumer fails open (no false no-plan state) exactly as
 * the claim-era code did while auth was still loading.
 */
export function useAccountPlan(): AccountPlanState {
  const { data } = useAccountUsage();

  // `undefined` = still loading; `null` = request failed. Neither is an answer.
  const resolved = data !== undefined && data !== null;

  return {
    plan: resolved ? data.plan ?? null : null,
    limits: resolved ? data.limits ?? {} : {},
    isResolved: resolved,
    isPending: data === undefined,
  };
}
